import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Sheet from 'components/utils/Sheet'
import cn from 'clsx'

const Container = styled.div`
  position: relative;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.default.one};
`

const BackgroundContainer = styled.div`
  position: absolute;
  inset: 0;
`

const ContentContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${rem(100)} ${rem(20)} ${rem(24)};
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;

  &.align-start {
    margin-bottom: auto;
  }

  &.align-center {
    margin-top: auto;
    margin-bottom: auto;
  }

  &.align-end {
    margin-top: auto;
  }

  &.align-space-between {
    flex: 1;
  }
`

const StyledSheet = styled(Sheet)`
  display: flex;
  flex-direction: column;

  .align-space-between > & {
    flex: 1;
    justify-content: space-between;
  }

  &.gap-8 {
    gap: ${rem(8)};
  }

  &.gap-12 {
    gap: ${rem(12)};
  }

  &.gap-16 {
    gap: ${rem(16)};
  }

  &.gap-20 {
    gap: ${rem(20)};
  }

  &.gap-24 {
    gap: ${rem(24)};
  }

  &.gap-32 {
    gap: ${rem(32)};
  }

  &.gap-60 {
    gap: ${rem(60)};
  }
`

interface Props {
  background?: ReactNode
  children: ReactNode
  className?: string
  /** Gap between children in pixels */
  gap?: 8 | 12 | 16 | 20 | 24 | 32 | 60
  verticalAlign?: 'start' | 'center' | 'end' | 'space-between'
}

function SlideBase({
  background,
  children,
  className,
  gap,
  verticalAlign = 'end',
}: Props) {
  return (
    <Container className={className}>
      {background && <BackgroundContainer>{background}</BackgroundContainer>}
      <ContentContainer>
        <ContentInner className={cn(`align-${verticalAlign}`)}>
          <StyledSheet paletteType="inverse" className={gap ? `gap-${gap}` : undefined}>
            {children}
          </StyledSheet>
        </ContentInner>
      </ContentContainer>
    </Container>
  )
}

export default React.memo(SlideBase)
